import { type ComponentProps, type ReactNode, useLayoutEffect, useRef } from 'react';

import { cn } from '@/lib/utils';

interface ScaledContentProps extends ComponentProps<'div'> {
  children: ReactNode;
  scale?: number;
}

export function ScaledContent({ children, scale = 0.4, className, ...props }: ScaledContentProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!scale || scale >= 1 || !contentRef.current || !wrapperRef.current) return;

    const updateHeight = () => {
      if (contentRef.current && wrapperRef.current) {
        const contentHeight = contentRef.current.scrollHeight;
        const scaledHeight = contentHeight * scale;
        wrapperRef.current.style.height = `${scaledHeight}px`;
      }
    };

    const resizeObserver = new ResizeObserver(updateHeight);
    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    updateHeight();

    return () => {
      resizeObserver.disconnect();
    };
  }, [scale]);

  if (scale < 1) {
    return (
      <div ref={wrapperRef} className={cn('relative overflow-hidden', className)} {...props}>
        <div
          ref={contentRef}
          className="origin-top-left"
          style={{
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            width: `${100 / scale}%`,
          }}
        >
          {children}
        </div>
      </div>
    );
  }

  return (
    <div className={className} {...props}>
      {children}
    </div>
  );
}
